// Full-text search over the notes, in memory. The notes mix pt-br and
// English, so there's no stemmer for either: terms are lowercased and their
// accents folded ("ação" and "acao" both become "acao"), and the query
// matches by prefix, with a little fuzziness for typos on longer words.
import MiniSearch from 'minisearch';

type Searchable = { id: string, title: string, body: string };

export const fold = (text: string) =>
  text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

// letters and digits in any script; everything else separates words
const tokenize = (text: string) => text.split(/[^\p{L}\p{N}]+/u);

const processTerm = (term: string) => fold(term) || null;

export const buildIndex = (notes: Searchable[]) => {
  const index = new MiniSearch<Searchable>({
    fields: ['title', 'body'],
    tokenize,
    processTerm,
    searchOptions: {
      boost: { title: 2 },
      prefix: true,
      fuzzy: term => (term.length > 4 ? 0.2 : false),
      combineWith: 'AND',
    },
  });
  index.addAll(notes);
  return index;
};

export type SearchIndex = ReturnType<typeof buildIndex>;

// an index kept in step with the notes across updates, re-indexing only those
// added, changed or gone since the last one - a full build over every body is
// a noticeable pause
export const liveIndex = () => {
  let index: SearchIndex | null = null;
  let indexed = new Map<string, Searchable>();

  // as of the last update; null before the first
  const current = () => index;

  const update = (notes: Searchable[]): SearchIndex => {
    const next = new Map(notes.map(n => [n.id, n]));
    if (!index) {
      index = buildIndex(notes);
    } else {
      const same = (a: Searchable, b: Searchable) =>
        a.title === b.title && a.body === b.body;
      const stale = [...indexed.values()]
        .filter(old => {
          const now = next.get(old.id);
          return !now || !same(old, now);
        });
      const fresh = notes.filter(n => {
        const old = indexed.get(n.id);
        return !old || !same(old, n);
      });
      stale.forEach(n => index!.discard(n.id));
      index.addAll(fresh);
    }
    indexed = next;
    return index;
  };

  return { current, update };
};

// the matching note ids, best first
export const search = (index: SearchIndex, query: string): string[] =>
  index.search(query).map(r => r.id as string);
